// Sample data for the BASYS approval queue demo (flag `approvals`). The real queue is ROC-87.
export type ApprovalStatus = "pending" | "approved" | "sent_back" | "rejected";
export type ApprovalType = "Quote" | "Discount request";

// Dot style in the case chain, as in v9: email received, done, email sent, error.
export type ApprovalStepKind = "email" | "ok" | "mail" | "err";

export interface ApprovalStep {
	label: string;
	kind?: ApprovalStepKind;
	detail?: string;
	when: string;
}

export interface Approval {
	id: string;
	merchant: string;
	template: string;
	partner: string;
	type: ApprovalType;
	requestedBy: string;
	// null when the viewer's role cannot see the limit.
	proposed: string | null;
	floor: string | null;
	vsFloor: string | null;
	belowFloor: boolean;
	note?: string;
	ageHours: number;
	status: ApprovalStatus;
	decisionNote?: string;
	reason: string;
	approver: string;
	// Not known for live requests yet; the review pane then hides the proposal preview.
	currentFees?: number;
	proposedFees?: number;
	chain: ApprovalStep[];
	// The API id of a live request (ROC-87); unset for sample rows.
	requestId?: string;
}

const chain = (change: string, approver: string): ApprovalStep[] => [
	{
		label: "Statement received",
		kind: "email",
		detail: "By email, 5 pages",
		when: "Yesterday",
	},
	{
		label: "Processed",
		kind: "ok",
		detail: "Analysis ready · 7 min 12 s",
		when: "Yesterday",
	},
	{ label: "Template applied", when: "Yesterday" },
	{ label: change, detail: "below the floor", when: "Yesterday" },
	{
		label: "Manager approval requested",
		kind: "mail",
		detail: `Email sent to ${approver}`,
		when: "Yesterday",
	},
];

export const APPROVALS: Approval[] = [
	{
		id: "RF-1083",
		merchant: "Stonebridge Tires",
		template: "Healthcare IC+",
		partner: "Lakeview Savings Bank",
		type: "Discount request",
		requestedBy: "Riley Park",
		proposed: "$18.00",
		floor: "$20.00",
		vsFloor: "−$2.00",
		belowFloor: true,
		ageHours: 1,
		status: "pending",
		reason: "Monthly fee $18.00 below floor $20.00",
		approver: "Dana Whitfield",
		currentFees: 4929.5,
		proposedFees: 3857.87,
		chain: chain("Monthly fee $18.00 changed", "Dana Whitfield"),
	},
	{
		id: "RF-1090",
		merchant: "Harbor Light Coffee",
		template: "Retail IC+",
		partner: "Summit Register Co.",
		type: "Discount request",
		requestedBy: "Riley Park",
		proposed: "0.24%",
		floor: "0.22%",
		vsFloor: "+0.02%",
		belowFloor: false,
		note: "outside rep range",
		ageHours: 3,
		status: "pending",
		reason: "Discount rate 0.24% is below the rep range 0.25% – 0.32%",
		approver: "Morgan Achebe",
		currentFees: 3120.4,
		proposedFees: 2604.12,
		chain: chain("Discount rate 0.24% changed", "Morgan Achebe"),
	},
	{
		id: "RF-1074",
		merchant: "Saltmarsh Seafood",
		template: "Healthcare IC+",
		partner: "Northgate POS",
		type: "Discount request",
		requestedBy: "Riley Park",
		proposed: null,
		floor: null,
		vsFloor: null,
		belowFloor: true,
		ageHours: 10,
		status: "pending",
		reason: "Deal margin below the floor",
		approver: "Dana Whitfield",
		currentFees: 2780.0,
		proposedFees: 2291.35,
		chain: chain("Deal margin changed", "Dana Whitfield"),
	},
	{
		id: "RF-1067",
		merchant: "Northstar Fitness",
		template: "Ecommerce Flat",
		partner: "Fieldstone Apps",
		type: "Discount request",
		requestedBy: "Riley Park",
		proposed: "$0.06",
		floor: "$0.08",
		vsFloor: "−$0.02",
		belowFloor: true,
		ageHours: 17,
		status: "pending",
		reason: "Authorization fee $0.06 below floor $0.08",
		approver: "Dana Whitfield",
		currentFees: 1984.2,
		proposedFees: 1702.66,
		chain: chain("Authorization fee $0.06 changed", "Dana Whitfield"),
	},
	{
		id: "RF-1048",
		merchant: "Cedar & Finch Market",
		template: "Retail IC+ standard",
		partner: "Prairie State Bank",
		type: "Quote",
		requestedBy: "Riley Park",
		proposed: "0.20%",
		floor: "0.22%",
		vsFloor: "−0.02%",
		belowFloor: true,
		ageHours: 26,
		status: "approved",
		reason: "Discount rate 0.20% below floor 0.22%",
		approver: "Dana Whitfield",
		currentFees: 5323.41,
		proposedFees: 4080.68,
		chain: chain("Discount rate 0.20% changed", "Dana Whitfield"),
	},
	{
		id: "RF-1033",
		merchant: "Northgate Auto Spa",
		template: "Retail IC+",
		partner: "Basys direct",
		type: "Quote",
		requestedBy: "Jordan Vale",
		proposed: "$0.00",
		floor: "$20.00",
		vsFloor: "−$20.00",
		belowFloor: true,
		ageHours: 120,
		status: "sent_back",
		reason: "Monthly fee waived below floor $20.00",
		approver: "Dana Whitfield",
		currentFees: 2410.0,
		proposedFees: 1895.5,
		chain: chain("Monthly fee waived", "Dana Whitfield"),
	},
	{
		id: "RF-1029",
		merchant: "Kestrel Books",
		template: "Retail Tiered",
		partner: "Keystone CPA Group",
		type: "Discount request",
		requestedBy: "Casey Nguyen",
		proposed: "0.18%",
		floor: "0.22%",
		vsFloor: "−0.04%",
		belowFloor: true,
		ageHours: 150,
		status: "rejected",
		reason: "Discount rate 0.18% below floor 0.22%",
		approver: "Dana Whitfield",
		currentFees: 1650.0,
		proposedFees: 1380.25,
		chain: chain("Discount rate 0.18% changed", "Dana Whitfield"),
	},
];

export const PENDING_APPROVALS = APPROVALS.filter(
	(a) => a.status === "pending",
).length;
